/**
 * Recording + upload schemas (AGENT.md §6.3, §11.4–11.5).
 *
 * A `Recording` row is the metadata for one captured/imported audio take. Audio
 * is per challenge + kind: one SEGMENT take (the concatenated dialogue, in the
 * team's language) and one QUESTION take (the English question). The audio bytes
 * themselves never travel through this API as JSON — they go to object storage
 * via a presigned URL (§11.4). So the lifecycle is:
 *
 *   1. capture/import on device → `POST /challenges/:id/recordings` (DRAFT row, with `kind`)
 *   2. choose the take          → `POST /recordings/:id/confirm`     (status CONFIRMED)
 *   3. request an upload URL     → `POST /recordings/:id/upload-url`  (presigned PUT)
 *   4. PUT the bytes to storage  (client → object storage directly)
 *   5. confirm the upload        → `POST /recordings/:id/confirm-upload` (sets audioObjectKey)
 *
 * The challenge can only leave RECORDING (→ READY_TO_TEST) once its SEGMENT take
 * (and its QUESTION take, when it has a question) is CONFIRMED and uploaded — see
 * `validateReadyToTest` in `../state-guards.ts`.
 *
 * FIREWALL: `audioObjectKey` is the one and only thing here that may reach the
 * model — it is branded `DataField` at the trusted boundary in the gateway
 * (§1.1). Everything else is recording metadata and is unrepresentable in a
 * `GeminiCallInput`.
 */

import { z } from 'zod';

/** Canonical audio format (§1.5): mono lossless WAV, 16 kHz or 48 kHz. */
export const CANONICAL_SAMPLE_RATE = 16000;
export const CANONICAL_CHANNELS = 1;
/** Accepted capture sample rates: 16 kHz (canonical) and 48 kHz (field devices). */
export const ACCEPTED_SAMPLE_RATES: readonly number[] = [16000, 48000];
/** True when a take's sample rate is one the pipeline accepts (§1.5). */
export const isAcceptedSampleRate = (hz: number): boolean => ACCEPTED_SAMPLE_RATES.includes(hz);

export const captureSourceSchema = z.enum(['APP_NATIVE', 'IMPORTED_FILE', 'DRIVE_IMPORT']);
export type CaptureSourceName = z.infer<typeof captureSourceSchema>;

export const recordingStatusSchema = z.enum(['DRAFT', 'CONFIRMED', 'REJECTED']);
export type RecordingStatusName = z.infer<typeof recordingStatusSchema>;

/** Which of a challenge's two takes this recording is (§0). */
export const recordingKindSchema = z.enum(['SEGMENT', 'QUESTION']);
export type RecordingKindName = z.infer<typeof recordingKindSchema>;

/**
 * Metadata of a captured/imported take. The Rust side down-mixes to mono at an
 * accepted rate (16 kHz or 48 kHz) before creating the row (§11.1–11.2), so a
 * row outside that set is a defect — the server rejects it rather than store
 * something the model pipeline can't trust.
 */
export const recordingCreateSchema = z.object({
  /** SEGMENT (the team-language dialogue) or QUESTION (the English question). */
  kind: recordingKindSchema.default('SEGMENT'),
  sampleRate: z
    .number()
    .int()
    .refine(isAcceptedSampleRate, {
      message: 'Audio must be 16 kHz or 48 kHz mono WAV (§1.5).',
    }),
  channels: z
    .number()
    .int()
    .refine((v) => v === CANONICAL_CHANNELS, {
      message: 'Audio must be mono — canonical storage is 16 kHz mono WAV (§1.5).',
    }),
  durationMs: z.number().int().positive('Recording duration must be greater than zero.'),
  captureSource: captureSourceSchema,
  /** e.g. "m4a-aac-44100-stereo"; the original before conversion (import only). */
  originalFormat: z.string().max(200).optional(),
  isLossySource: z.boolean().default(false),
});
export type RecordingCreateInput = z.infer<typeof recordingCreateSchema>;
export type RecordingCreateRequest = z.input<typeof recordingCreateSchema>;

/** Body for `POST /recordings/:id/upload-url`. The whole body is optional. */
export const uploadUrlRequestSchema = z
  .object({
    contentType: z.string().max(120).default('audio/wav'),
  })
  .default({});
export type UploadUrlRequest = z.infer<typeof uploadUrlRequestSchema>;
export type UploadUrlRequestInput = z.input<typeof uploadUrlRequestSchema>;

/** Response from `POST /recordings/:id/upload-url`. */
export interface UploadUrlResponse {
  recordingId: string;
  /** Canonical object key (§11.5) the bytes will live at. */
  objectKey: string;
  /** Presigned URL the client uploads the WAV to (single-part PUT). */
  uploadUrl: string;
  method: 'PUT';
  /** Headers the client must echo on the PUT for the signature to match. */
  headers: Record<string, string>;
  expiresInSeconds: number;
}

/**
 * Result of claiming/releasing the QUESTION take of a challenge — the parallel of
 * the segment `claim` (which sets `currentAssigneeId`). Advisory: it marks who is
 * recording the English question so two question recorders don't duplicate it; it
 * does NOT change challenge state and never gates the sync's ingest.
 */
export interface QuestionClaimResult {
  challengeId: string;
  /** The user now holding the question take, or null after a release. */
  questionAssigneeId: string | null;
}

/** Recorder/queue view of a single take. Carries no challenge gold fields. */
export interface RecordingSummary {
  id: string;
  challengeId: string;
  kind: RecordingKindName;
  /** Legacy per-utterance link; null for per-challenge recordings. */
  utteranceId: string | null;
  recorderId: string;
  /** True once the bytes are uploaded (audioObjectKey set). */
  uploaded: boolean;
  audioObjectKey: string | null;
  sampleRate: number;
  channels: number;
  durationMs: number;
  captureSource: CaptureSourceName;
  originalFormat: string | null;
  isLossySource: boolean;
  status: RecordingStatusName;
  uploadProgress: number;
  createdAt: string;
}
